'use client';

import { useEffectOnce } from '@carvana/merch-react-hooks';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type React from 'react';

export type SrpSkeletonProps = {
  isInPriceDropSuppressionTreatment: boolean;
  shouldShowComposer: boolean;
};

type SrpNavigationContextType = {
  dismissSrpNavigation: () => void;
  srpSkeletonProps: SrpSkeletonProps | null;
  triggerSrpNavigation: (props: SrpSkeletonProps) => void;
};

const SrpNavigationContext = createContext<SrpNavigationContextType>({
  dismissSrpNavigation: () => {},
  srpSkeletonProps: null,
  triggerSrpNavigation: () => {}
});

export function SrpNavigationContextProvider({
  children
}: React.PropsWithChildren) {
  const [srpSkeletonProps, setSrpSkeletonProps] =
    useState<SrpSkeletonProps | null>(null);

  const dismissSrpNavigation = useCallback(() => setSrpSkeletonProps(null), []);
  const triggerSrpNavigation = useCallback(
    (props: SrpSkeletonProps) => setSrpSkeletonProps(props),
    []
  );

  useEffectOnce(() => {
    if (typeof window === 'undefined') return;

    function handleRestore() {
      setSrpSkeletonProps(null);
    }

    window.addEventListener('popstate', handleRestore);
    window.addEventListener('pageshow', handleRestore);

    return () => {
      window.removeEventListener('popstate', handleRestore);
      window.removeEventListener('pageshow', handleRestore);
    };
  }, []);

  const value = useMemo(
    () => ({ dismissSrpNavigation, srpSkeletonProps, triggerSrpNavigation }),
    [dismissSrpNavigation, srpSkeletonProps, triggerSrpNavigation]
  );

  return (
    <SrpNavigationContext.Provider value={value}>
      {children}
    </SrpNavigationContext.Provider>
  );
}

export function useDismissSrpNavigation() {
  return useContext(SrpNavigationContext).dismissSrpNavigation;
}

export function useSrpSkeletonProps() {
  return useContext(SrpNavigationContext).srpSkeletonProps;
}

export function useTriggerSrpNavigation() {
  return useContext(SrpNavigationContext).triggerSrpNavigation;
}
